import React, { useState, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import NewAppointmentNote from 'components/HealthRecords/AppointmentNotes/NewAppointmentNote';
import './style.scss';
import SelectableListItem from 'components/Utils/SelectableListItem';
import List from '@material-ui/core/List';
import AppointmentNoteBadge from 'components/HealthRecords/AppointmentNotes/AppointmentNoteBadge';
import PerformanceUtils from 'services/PerformanceUtils';
import { NoteContent } from 'components/HealthRecords/AppointmentNotes/AppointmentNoteDetails';
import { useAppSelector } from 'careand-redux/store';
import CalendarUtils from 'services/CalendarUtils';
import moment from 'moment-timezone';
import ResponsiveColumns from 'components/ResponsiveColumns';

const AppointmentNoteScreen = (props) => {
  return (
    <div className="appointment-note-screen">
      <NewAppointmentNote
        appointmentId={props.bubble?.appointment_id}
        patient={props.patient}
        onClose={props.onClose}
        overrideFormId={props.overrideFormId}
        askProactiveCarey={props.askProactiveCarey}
      />
    </div>
  );
};

const DEFAULT_OBJECT = {};

export const NoteList = ({ patient }) => {
  const healthRecords =
    useAppSelector((state) => state.healthRecords.byId[patient.id]) || DEFAULT_OBJECT;
  const physicians = useAppSelector((state) => state.physicians.byId);
  const patientAppointments = useAppSelector((state) => state.appointments.byId[patient.id]);
  const ownId = useAppSelector((state) => state.authentication.user.id);
  const navigateTo = useRef();

  const patientDocuments = healthRecords.documents;

  const [selected, setSelected] = useState();

  const notes = useMemo(
    () => PerformanceUtils.sortBy(healthRecords.notes, ['value.date']),
    [healthRecords.notes],
  );

  const draftNotes = useMemo(
    () =>
      PerformanceUtils.sortBy(healthRecords.appointmentNoteDrafts, ['value.date'])
        .filter((draft) => draft.author_id !== ownId)
        .map((draft) => ({
          ...draft.note,
          author_id: draft.author_id,
          id: draft.id,
          isDraft: true,
        })),
    [healthRecords.appointmentNoteDrafts, ownId],
  );

  const allNotes = useMemo(() => {
    return PerformanceUtils.sortBy([...notes, ...draftNotes], 'value.date');
  }, [notes, draftNotes]);

  const noteAppointments = useMemo(() => {
    let sortedAppointments = PerformanceUtils.sortBy(patientAppointments, ['date']);
    let result = {};

    allNotes.forEach((note) => {
      let closest;
      let distance;
      sortedAppointments
        .filter((appointment) => appointment.date - 60 * 60 < note.value.date)
        .forEach((appointment) => {
          if (!closest) {
            closest = appointment;
            distance = Math.abs(appointment.date - note.value.date);
          } else {
            let newDistance = Math.abs(appointment.date - note.value.date);
            if (newDistance < distance) {
              distance = newDistance;
              closest = appointment;
            }
          }
        });
      const isSameDay = CalendarUtils.isSameDay(
        moment.unix(closest?.date),
        moment.unix(note.value.date),
      );
      if (isSameDay) result[note.id] = closest;
    });
    return result;
  }, [patientAppointments, allNotes]);

  const noteDocuments = useMemo(() => {
    let result = {};
    let sortedDocuments = PerformanceUtils.sortBy(patientDocuments, ['date']);

    allNotes.forEach((note) => {
      let toIterate = [...sortedDocuments];
      let inserted = [];
      toIterate.forEach((doc) => {
        if (doc.date >= note.value.date) {
          result[note.id] = result[note.id] || [];
          result[note.id].push(doc);
          inserted.push(doc.id);
        }
      });
      sortedDocuments = sortedDocuments.filter((doc) => !inserted.includes(doc.id));
    });
    return result;
  }, [patientDocuments, allNotes]);

  function onClick(note) {
    setSelected(note);
    setTimeout(() => {
      navigateTo.current?.(1);
    }, 100);
  }

  function getFirstLine(note) {
    let appointment = noteAppointments[note.id];
    if (note.isDraft) return 'Draft';
    if (appointment) {
      let result = appointment.type;
      if (appointment.concern_text) {
        result += ` - ${appointment.concern_text}`;
      }
      return result;
    } else {
      return 'Administrative Note';
    }
  }

  return (
    <ResponsiveColumns
      className="note-list"
      navigateTo={navigateTo}
      columns={[
        {
          name: 'Note List',
          element: (
            <List className="underside-right-panel menu list">
              <span className="small-title padded-title">Notes</span>
              {allNotes.map((note) => (
                <SelectableListItem
                  key={note.id}
                  onClick={() => onClick(note)}
                  selected={selected?.id === note.id}
                  item={
                    <AppointmentNoteBadge
                      item={note}
                      firstLine={getFirstLine(note)}
                      count={noteDocuments[note.id]?.length}
                    />
                  }
                />
              ))}
              {allNotes.length === 0 && <span className="no-records">No records to show</span>}
            </List>
          ),
        },
        selected
          ? {
              name: 'Note Details',
              element: (
                <div className="note-viewer full-height-panel scroll-list underside-right-panel">
                  {selected?.value?.notes !== undefined && (
                    <span>Notes: {selected?.value?.notes}</span>
                  )}
                  <NoteContent
                    forceOldNoteStyle
                    physicians={physicians}
                    note={selected}
                    showInfoText={false}
                    documents={noteDocuments[selected?.id]}
                  />
                </div>
              ),
            }
          : undefined,
      ]}
    />
  );
};

AppointmentNoteScreen.propTypes = {
  form: PropTypes.string,
  patient: PropTypes.object.isRequired,
  onClose: PropTypes.func,
  bubble: PropTypes.object,
};

export default AppointmentNoteScreen;
